import { call, takeLatest, put, select } from 'redux-saga/effects';
import { isEmpty } from 'lodash';
import { addToRootSaga } from 'store';
import { fetchPerformantCampaigns } from 'api/IterableGoalsAPI';

import { Saga } from 'custom-types/saga';
import { PerformantCampaignAction, PerformantCampaignQuery } from 'modules/iterableGoals/types';
import { addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { getCommonTranslations } from 'utils/translations';
import * as actions from 'modules/iterableGoals/ducks/actions';
import { ActionTypes } from 'modules/iterableGoals/ducks/actionTypes';
import { getPerformantCampaignState } from '../selectors';

// Temporary caching mechanism
const cachedQueryData: Record<string, string | undefined> = {
  startTime: '',
  endTime: '',
};

export function* fetchPerformantCampaignSaga({
  queryData,
}: PerformantCampaignAction<{
  queryData: PerformantCampaignQuery;
}>): Saga<PerformantCampaignAction> {
  try {
    const { data: performantCampaignsStateData } = yield select(getPerformantCampaignState);
    let performantCampaigns = performantCampaignsStateData;

    const hasTimeRangeChanged =
      cachedQueryData.startTime !== queryData?.startTime ||
      cachedQueryData.endTime !== queryData?.endTime;
    const isDataNotAvailable =
      isEmpty(performantCampaignsStateData?.best) || isEmpty(performantCampaignsStateData?.worst);

    if (hasTimeRangeChanged || isDataNotAvailable) {
      const { data } = yield call(fetchPerformantCampaigns, queryData);
      performantCampaigns = data.bestWorstCampaignStatsForGoal;

      // Updating the time range in the redux state
      cachedQueryData.startTime = queryData?.startTime;
      cachedQueryData.endTime = queryData?.endTime;
    }

    yield put(actions.fetchPerformantCampaignResponse(performantCampaigns));
  } catch (error) {
    const errorMessage = yield call(getCommonTranslations, 'errorMessage.contactCSM');
    yield put(actions.fetchPerformantCampaignError());
    yield put(
      addNegativeToast(
        errorMessage,
        errorIdentifier({
          source: 'fetchPerformantCampaignSaga',
          error,
        }),
      ),
    );
  }
}

addToRootSaga([
  takeLatest(ActionTypes.FETCH_PERFORMANT_CAMPAIGNS_REQUEST, fetchPerformantCampaignSaga),
]);
